"use client";

import { updateUserLanguage } from "@/actions/language";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "@/i18n/routing";
import { signOut } from "@/lib/firebase-auth";
import {
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import { TeamNavItem, TournamentNavItem } from "./PublicHeader";

interface UserMenuProps {
  userName: string;
  className?: string;
  tournamentNavItems: TournamentNavItem[];
  teamNavItems: TeamNavItem[];
  isOrganizer?: boolean;
}
export const getInitials = (name?: string) => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const HeaderButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ children, className, asChild, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        `avatar w-auto min-w-10 px-2 h-10 bg-gradient-to-br from-[var(--gold)] to-[var(--gold-dark)] rounded-[10px] flex items-center justify-center font-cinzel font-bold text-[0.85rem] text-[var(--bg-deep)] border-2 border-[var(--gold-light)] ml-1 cursor-pointer transition-[transform,box-shadow] hover:scale-105 hover:bg-[rgba(184,134,11,0.15)] hover:border-[var(--gold)] hover:shadow-[0_8px_20px_-4px_rgba(184,134,11,0.6)]`,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

HeaderButton.displayName = "HeaderButton";

export function UserMenu({
  userName,
  className,
  tournamentNavItems,
  teamNavItems,
  isOrganizer = false,
}: UserMenuProps) {
  const t = useTranslations();
  const { user } = useAuth();
  const locale = useLocale() as "fi" | "en";

  const handleLanguageSwitch = async () => {
    const newLocale = locale === "fi" ? "en" : "fi";

    // Update language in publicProfile if user is logged in
    if (user?.id) {
      await updateUserLanguage(newLocale);
    }

    // Get the current browser URL path and swap the locale prefix
    const currentPath = window.location.pathname;
    let newPath: string;

    // Check if current path starts with a locale prefix
    if (currentPath.startsWith("/fi/") || currentPath.startsWith("/en/")) {
      // Replace existing locale prefix
      newPath = `/${newLocale}${currentPath.slice(3)}`;
    } else if (currentPath === "/fi" || currentPath === "/en") {
      // Handle root with locale
      newPath = `/${newLocale}`;
    } else {
      // No locale prefix, add the new one
      newPath = `/${newLocale}${currentPath}`;
    }

    // Use hard navigation to reload with new locale messages
    window.location.href = newPath;
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/";
  };

  return (
    <Dropdown
      placement="bottom-end"
      shouldBlockScroll={false}
      portalContainer={
        typeof window !== "undefined" ? document.body : undefined
      }
    >
      <DropdownTrigger asChild>
        <HeaderButton className={className}>
          {getInitials(userName)}
        </HeaderButton>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={t("header.userMenu.title")}
        className="w-[240px]"
        disabledKeys={["name"]}
      >
        <DropdownSection showDivider>
          <DropdownItem key="name" className="text-center text-foreground">
            - {userName} -
          </DropdownItem>

          <DropdownItem
            key="profile"
            as={Link}
            href="/profile"
            startContent={<Icon icon="game-icons:crowned-skull" width={20} />}
          >
            {t("header.userMenu.profile")}
          </DropdownItem>
        </DropdownSection>
        {teamNavItems && teamNavItems.length > 0 ? (
          <DropdownSection
            showDivider
            title={t("header.userMenu.myTeams")}
            classNames={{
              heading:
                "pl-2 pb-1 text-tiny uppercase font-bold text-default-500",
            }}
          >
            {teamNavItems.map((team) => (
              <DropdownItem
                key={team.teamId}
                as={Link}
                href={`/tournaments/${team.tournamentSlug}/my-team`}
                startContent={
                  <Icon icon="game-icons:swords-power" width={20} />
                }
              >
                {team.teamName || team.tournamentName}
              </DropdownItem>
            ))}
          </DropdownSection>
        ) : null}
        {tournamentNavItems && tournamentNavItems.length > 0 ? (
          <DropdownSection
            showDivider
            title={t("header.userMenu.myTournaments")}
            classNames={{
              heading:
                "pl-2 pb-1 text-tiny uppercase font-bold text-default-500",
            }}
          >
            {tournamentNavItems.map((item) => {
              const tournamentId = Object.keys(item)[0];
              const tournament = item[tournamentId];
              return (
                <DropdownItem
                  key={tournamentId}
                  as={Link}
                  href={`/tournaments/${tournament.slug}`}
                  startContent={
                    <Icon icon="game-icons:stone-tower" width={20} />
                  }
                >
                  {tournament.name}
                </DropdownItem>
              );
            })}
          </DropdownSection>
        ) : null}
        {isOrganizer ? (
          <DropdownSection showDivider>
            <DropdownItem
              key="language"
              href="/admin"
              startContent={
                <Icon icon="game-icons:byzantin-temple" width={20} />
              }
            >
              {t("header.userMenu.adminPanel")}
            </DropdownItem>
          </DropdownSection>
        ) : null}

        <DropdownSection showDivider>
          <DropdownItem
            key="language"
            onPress={handleLanguageSwitch}
            startContent={<Icon icon="game-icons:wireframe-globe" width={20} />}
          >
            {locale === "fi" ? "In English" : "Suomeksi"}
          </DropdownItem>
        </DropdownSection>

        <DropdownSection>
          <DropdownItem
            key="logout"
            onPress={handleSignOut}
            className="text-danger"
            color="danger"
            startContent={<Icon icon="game-icons:exit-door" width={20} />}
          >
            {t("header.userMenu.logout")}
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
}
